class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // Update cart note with embroidery details from cart items
    this.updateCartNoteWithEmbroidery();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  updateCartNoteWithEmbroidery() {
    // Get cart data and check for embroidery properties
    if (typeof fetch === 'undefined' || typeof routes === 'undefined') return;
    
    fetch(`${routes.cart_url}.js`)
      .then(response => response.json())
      .then(cart => {
        if (!cart.items || cart.items.length === 0) return;
        
        const embroideryData = [];
        cart.items.forEach(item => {
          if (item.properties) {
            const embroideryText = item.properties['Embroidery Text'];
            const embroideryPosition = item.properties['Embroidery Position'] || 'Center';
            
            if (embroideryText && embroideryText.trim()) {
              embroideryData.push({
                text: embroideryText.trim(),
                position: embroideryPosition
              });
            }
          }
        });
        
        if (embroideryData.length > 0) {
          let currentNote = cart.note || '';
          let currentAttributes = cart.attributes || {};
          let noteUpdated = false;
          let attributesUpdated = false;
          
          // Collect all unique embroidery texts for attributes (use first one if multiple)
          const firstEmbroidery = embroideryData[0];
          
          // Check if attributes need updating
          if (currentAttributes['Embroidery Text'] !== firstEmbroidery.text || 
              currentAttributes['Embroidery Position'] !== firstEmbroidery.position) {
            attributesUpdated = true;
          }
          
          // Update note for each embroidery entry
          embroideryData.forEach(embData => {
            const note = `Embroidery: ${embData.text}\nPosition: ${embData.position}`;
            // Check if this embroidery text already exists to avoid duplicates
            if (!currentNote.includes(`Embroidery: ${embData.text}`)) {
              currentNote = currentNote ? `${currentNote}\n${note}` : note;
              noteUpdated = true;
            }
          });
          
          // Update both note and attributes if needed
          if (noteUpdated || attributesUpdated) {
            const updatePayload = {};
            if (noteUpdated) {
              updatePayload.note = currentNote;
            }
            if (attributesUpdated) {
              updatePayload.attributes = {
                ...currentAttributes,
                'Embroidery Text': firstEmbroidery.text,
                'Embroidery Position': firstEmbroidery.position
              };
            }
            
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ 
                body: JSON.stringify(updatePayload)
              }
            }).catch(e => console.error('Failed to update cart note/attributes from drawer:', e));
          }
        }
      })
      .catch(e => console.error('Failed to fetch cart for note update:', e));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
